import { useState } from 'react'

import { SystemSpaceSlug } from '@bettermode/common/space'
import { isNotFound } from '@tribeplatform/gql-client/lib'
import { Space } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useSpace } from '@tribeplatform/react-sdk/hooks'
import { HelperText } from '@tribeplatform/react-ui-kit/HelperText'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { DefaultHomepageSettingsFieldProps } from '../types.js'

export const DefaultHomepageFormField = ({
  formKey,
  defaultValue,
  isLoading,
  onChange,
}: DefaultHomepageSettingsFieldProps) => {
  const { $t } = useI18n()
  const { data: systemHomeSpace } = useSpace({
    variables: { slug: SystemSpaceSlug.HOME },
    useQueryOptions: {
      refetchOnWindowFocus: false,
      retry(_, error) {
        return !isNotFound(error)
      },
    },
  })

  const [currentValue, setCurrentValue] = useState<Space>(defaultValue)

  return (
    <Form.SpacePicker
      name={formKey}
      value={currentValue}
      disabled={isLoading}
      staticOptions={systemHomeSpace ? [systemHomeSpace] : undefined}
      label={$t({
        defaultMessage: 'Default homepage for logged-out visitors',
        id: 'Admin.HomepageSettings.DefaultHomepage',
      })}
      clearable={false}
      onChange={(value: Space) => {
        if (value?.id) {
          setCurrentValue(value)
          if (onChange) {
            onChange(value)
          }
        }
      }}
      helperText={<DefaultHomepageInputHelperText space={currentValue} />}
      additionalIndicatorRenderer={space => {
        if (space.private) {
          return <SvgIcon className="text-content-subdued" name="lock" />
        }
        return null
      }}
    />
  )
}

const DefaultHomepageInputHelperText = (props: { space: Space }) => {
  const { space } = props
  if (space?.private) {
    return (
      <HelperText status="error" highlighted className="mt-2">
        <div className="flex items-center space-x-2">
          <SvgIcon name="status-warning" />
          <span>
            <T
              defaultMessage="{space_name} is a private space and cannot be set as the default homepage."
              values={{
                space_name: <strong>{space.name}</strong>,
              }}
              id="Admin.HomepageSettings.PrivacyWarning"
            />
          </span>
        </div>
      </HelperText>
    )
  }
  return (
    <p>
      <T
        defaultMessage="Choose the page for public visitors who haven't logged in. If your {COMMUNITY} is private, they will be redirected to the login page."
        id="Admin.HomepageSettings.DefaultHomepageHelperText"
      />
    </p>
  )
}
